import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientService } from '../services/api';
import { Users, Plus, Phone, Mail, Building, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import Notification from '../components/common/Notification';
import useNotification from '../hooks/useNotification';
import useModal from '../hooks/useModal';
import ClientIntakeModal from '../components/intake/ClientIntakeModal';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  closed: { label: 'Closed', color: 'bg-red-100 text-red-800' },
  intake: { label: 'Intake', color: 'bg-blue-100 text-blue-800' },
  not_hired: { label: 'Not Hired', color: 'bg-gray-100 text-gray-600' },
};

const PAGE_SIZE = 50;

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(() =>
    localStorage.getItem('jp_clients_status_filter') || 'active'
  );
  const [createForm, setCreateForm] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: 'United States',
    client_type: 'individual',
    notes: ''
  });
  const { notification, showSuccess, showError, showWarning, hideNotification } = useNotification();
  const { handleBackdropClick } = useModal(showCreateModal, () => setShowCreateModal(false));
  const debounceRef = useRef(null);

  const fetchClients = useCallback(async (search, status, page) => {
    try {
      const params = { limit: PAGE_SIZE, offset: page };
      if (status && status !== 'all') params.status_filter = status;
      if (search) params.search = search;
      const response = await clientService.getClients(params);
      setClients(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setInitialLoad(false);
    }
  }, []);

  // Fetch when filters or page change
  useEffect(() => {
    fetchClients(searchQuery, statusFilter, offset);
  }, [searchQuery, statusFilter, offset, fetchClients]);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setOffset(0);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  // Persist filter preferences
  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setOffset(0);
    localStorage.setItem('jp_clients_status_filter', status);
  };

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleCreateClient = async () => {
    try {
      // Validate required fields
      if (!createForm.first_name && !createForm.last_name && !createForm.company_name) {
        showWarning('Please provide at least one of: First Name, Last Name, or Company Name');
        return;
      }

      const clientData = {
        ...createForm
      };

      // Remove empty string fields (backend expects null for optional fields)
      Object.keys(clientData).forEach(key => {
        if (clientData[key] === '') {
          clientData[key] = null;
        }
      });

      await clientService.createClient(clientData);
      setShowCreateModal(false);
      setCreateForm({
        first_name: '',
        last_name: '',
        company_name: '',
        email: '',
        phone: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: 'United States',
        client_type: 'individual',
        notes: ''
      });
      await fetchClients(searchQuery, statusFilter, offset);
      showSuccess('Client created successfully!');
    } catch (error) {
      console.error('Error creating client:', error);
      showError(`Failed to create client: ${error.response?.data?.detail || error.message}`);
    }
  };

  if (initialLoad) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Users className="mr-3 h-8 w-8" />
            Clients
          </h1>
          <p className="text-gray-600">Manage your client relationships</p>
        </div>
        <button
          onClick={() => setShowIntakeModal(true)}
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-yellow-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Client
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or phone number..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {[
              { key: 'active', label: 'Active' },
              { key: 'closed', label: 'Closed' },
              { key: 'intake', label: 'Intake' },
              { key: 'not_hired', label: 'Not Hired' },
              { key: 'all', label: 'All' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleStatusChange(key)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  statusFilter === key
                    ? 'bg-yellow-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              {total.toLocaleString()} client{total !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Clients Table */}
      {clients.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trust Balance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {clients.map((client) => (
                  <tr
                    key={client.id}
                    onClick={() => navigate(`/clients/${client.id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="bg-gray-100 p-2 rounded-full">
                            {client.client_type === 'business' ? (
                              <Building className="h-6 w-6 text-yellow-600" />
                            ) : (
                              <Users className="h-6 w-6 text-yellow-600" />
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {client.company_name || `${client.first_name} ${client.last_name}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                        STATUS_CONFIG[client.status]?.color || 'bg-gray-100 text-gray-600'
                      }`}>
                        {STATUS_CONFIG[client.status]?.label || client.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 capitalize">{client.client_type}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-600">
                        {client.email ? (
                          <>
                            <Mail className="h-4 w-4 text-gray-400 mr-2" />
                            {client.email}
                          </>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-600">
                        {client.phone ? (
                          <>
                            <Phone className="h-4 w-4 text-gray-400 mr-2" />
                            {client.phone}
                          </>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-green-600">
                        ${client.trust_balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Showing {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
                className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500">
            {searchInput ? 'No clients match your search' : 'No clients found'}
          </p>
        </div>
      )}

      {/* Create Client Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleBackdropClick}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Create New Client</h3>

            <div className="space-y-4 mb-6">
              {/* Client Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Type *</label>
                <select
                  value={createForm.client_type}
                  onChange={(e) => setCreateForm({...createForm, client_type: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="individual">Individual</option>
                  <option value="business">Business</option>
                  <option value="organization">Organization</option>
                </select>
              </div>

              {/* Names */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={createForm.first_name}
                    onChange={(e) => setCreateForm({...createForm, first_name: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={createForm.last_name}
                    onChange={(e) => setCreateForm({...createForm, last_name: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={createForm.company_name}
                  onChange={(e) => setCreateForm({...createForm, company_name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Leave blank for individuals"
                />
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm({...createForm, phone: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>

              {/* Address Fields */}
              <div className="space-y-4 border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-900">Address</h4>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                  <input
                    type="text"
                    value={createForm.address_line1}
                    onChange={(e) => setCreateForm({...createForm, address_line1: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="Street address, P.O. box, company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                  <input
                    type="text"
                    value={createForm.address_line2}
                    onChange={(e) => setCreateForm({...createForm, address_line2: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="Apartment, suite, unit, building, floor (optional)"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={createForm.city}
                      onChange={(e) => setCreateForm({...createForm, city: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State / Province / Region</label>
                    <input
                      type="text"
                      value={createForm.state_province}
                      onChange={(e) => setCreateForm({...createForm, state_province: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP / Postal Code</label>
                    <input
                      type="text"
                      value={createForm.postal_code}
                      onChange={(e) => setCreateForm({...createForm, postal_code: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input
                      type="text"
                      value={createForm.country}
                      onChange={(e) => setCreateForm({...createForm, country: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({...createForm, notes: e.target.value})}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Additional notes about this client"
                />
              </div>

              <div className="text-sm text-gray-600">
                * At least one of First Name, Last Name, or Company Name is required
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleCreateClient}
                className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
              >
                Create Client
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateForm({
                    first_name: '',
                    last_name: '',
                    company_name: '',
                    email: '',
                    phone: '',
                    address_line1: '',
                    address_line2: '',
                    city: '',
                    state_province: '',
                    postal_code: '',
                    country: 'United States',
                    client_type: 'individual',
                    notes: ''
                  });
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client Intake Modal */}
      <ClientIntakeModal
        isOpen={showIntakeModal}
        onClose={() => setShowIntakeModal(false)}
        onSuccess={(newClient) => {
          setShowIntakeModal(false);
          if (newClient?.id) {
            navigate(`/clients/${newClient.id}`);
          } else {
            fetchClients(searchQuery, statusFilter, offset);
          }
          showSuccess('Client created successfully!');
        }}
      />

      {/* Notification */}
      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default Clients;
